import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  Building2,
  UserPlus,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

import { useAuth } from "@/auth/AuthProvider";
import { apiFetch, type ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Lakshadweep",
  "Delhi",
  "Puducherry",
  "Ladakh",
  "Jammu and Kashmir",
];

// Validation helpers
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PHONE_REGEX = /^\+?[0-9]{10,15}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FieldErrors = Record<string, string>;

function validateCreateForm(form: {
  displayName: string;
  orgName: string;
  legalName: string;
  addressLine1: string;
  city: string;
  state: string;
  pincode: string;
  gstin: string;
  contactPhone: string;
  contactEmail: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (form.displayName.trim().length < 2) {
    errors.displayName = "Name must be at least 2 characters";
  }
  if (form.orgName.trim().length < 2) {
    errors.orgName = "Organization name must be at least 2 characters";
  }
  if (form.legalName.trim().length < 2) {
    errors.legalName = "Legal name must be at least 2 characters";
  }
  if (form.addressLine1.trim().length < 5) {
    errors.addressLine1 = "Address must be at least 5 characters";
  }
  if (form.city.trim().length < 2) {
    errors.city = "City is required";
  }
  if (!form.state) {
    errors.state = "State is required";
  }
  if (!/^\d{6}$/.test(form.pincode)) {
    errors.pincode = "Pincode must be 6 digits";
  }
  if (form.gstin && !GSTIN_REGEX.test(form.gstin.toUpperCase())) {
    errors.gstin = "Invalid GSTIN format (e.g., 22AAAAA0000A1Z5)";
  }
  const phone = form.contactPhone.replace(/[\s-]/g, "");
  if (!PHONE_REGEX.test(phone)) {
    errors.contactPhone = "Invalid phone (10-15 digits)";
  }
  if (!EMAIL_REGEX.test(form.contactEmail)) {
    errors.contactEmail = "Invalid email address";
  }

  return errors;
}

function validateJoinForm(form: {
  displayName: string;
  code: string;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (form.displayName.trim().length < 2) {
    errors.displayName = "Name must be at least 2 characters";
  }
  if (form.code.length !== 6) {
    errors.code = "Code must be 6 characters";
  }

  return errors;
}

function parseApiError(err: unknown): string {
  // Handle our ApiError type
  if (err && typeof err === "object" && "body" in err) {
    const apiErr = err as ApiError;
    const body = apiErr.body;

    // If we have validation details, format them nicely
    if (body?.details && Array.isArray(body.details)) {
      const messages = body.details.map(
        (d: { path: string; message: string }) => `${d.path}: ${d.message}`,
      );
      return messages.join("\n");
    }

    // Otherwise use the message
    if (body?.message) return body.message;
    if (body?.error) return body.error;
  }

  // Fallback for regular errors
  if (err instanceof Error) return err.message;

  return "An unexpected error occurred";
}

function FieldError({ error }: { error?: string }) {
  if (!error) return null;
  return (
    <p className="mt-1 flex items-center gap-1 text-xs text-red-400">
      <AlertCircle className="h-3 w-3" />
      {error}
    </p>
  );
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, claim } = useAuth();
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState("create");

  // Create Org Form State
  const [createForm, setCreateForm] = useState({
    displayName: "",
    orgName: "",
    legalName: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    gstin: "",
    contactPhone: "",
    contactEmail: "",
  });
  const [createErrors, setCreateErrors] = useState<FieldErrors>({});

  // Join Org Form State
  const [joinForm, setJoinForm] = useState({
    displayName: "",
    code: "",
  });
  const [joinErrors, setJoinErrors] = useState<FieldErrors>({});

  // Initialize form with user data when available
  useState(() => {
    if (user) {
      setCreateForm((f) => ({
        ...f,
        displayName: user.displayName || "",
        contactEmail: user.email || "",
      }));
      setJoinForm((f) => ({
        ...f,
        displayName: user.displayName || "",
      }));
    }
  });

  async function handleCreateOrg(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;

    // Frontend validation
    const errors = validateCreateForm(createForm);
    setCreateErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.error("Please fix the errors below");
      return;
    }

    setBusy(true);

    try {
      await apiFetch("/api/onboarding/create-org", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(createForm),
      });

      // Force token refresh to get new claims
      if (user) await user.getIdToken(true);

      toast.success("Organization created successfully!");
      navigate("/dashboard");
    } catch (err: unknown) {
      const message = parseApiError(err);
      toast.error("Failed to create organization", {
        description: message,
        duration: 6000,
      });

      // Try to set field-specific errors from API response
      if (err && typeof err === "object" && "body" in err) {
        const body = (err as ApiError).body;
        if (body?.details && Array.isArray(body.details)) {
          const apiErrors: FieldErrors = {};
          for (const d of body.details) {
            if (d.path) apiErrors[d.path] = d.message;
          }
          setCreateErrors(apiErrors);
        }
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleJoinOrg(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;

    // Frontend validation
    const errors = validateJoinForm(joinForm);
    setJoinErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.error("Please fix the errors below");
      return;
    }

    setBusy(true);

    try {
      await apiFetch("/api/onboarding/join-org", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(joinForm),
      });

      // Force token refresh to get new claims
      if (user) await user.getIdToken(true);

      toast.success("Joined organization successfully!");
      navigate("/dashboard");
    } catch (err: unknown) {
      const message = parseApiError(err);
      toast.error("Failed to join organization", {
        description: message,
        duration: 6000,
      });

      // Try to set field-specific errors from API response
      if (err && typeof err === "object" && "body" in err) {
        const body = (err as ApiError).body;
        if (body?.details && Array.isArray(body.details)) {
          const apiErrors: FieldErrors = {};
          for (const d of body.details) {
            if (d.path) apiErrors[d.path] = d.message;
          }
          setJoinErrors(apiErrors);
        }
      }
    } finally {
      setBusy(false);
    }
  }

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  // Not logged in - redirect to login first
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Already onboarded - redirect to appropriate dashboard
  if (claim) {
    const dashboardPath =
      claim.role === "kioskconsumer" ? "/kiosk" : "/dashboard";
    return <Navigate to={dashboardPath} replace />;
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-slate-950 px-4 py-12 selection:bg-purple-500/30">
      {/* Background Elements */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[20%] left-0 h-[1000px] w-[1000px] -translate-x-1/2 rounded-full bg-purple-900/10 blur-[100px]" />
        <div className="absolute top-[20%] right-0 h-[800px] w-[800px] translate-x-1/3 rounded-full bg-indigo-900/10 blur-[100px]" />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150"></div>

      <div className="relative z-10 w-full max-w-2xl animate-in fade-in slide-in-from-bottom-8 duration-700">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Welcome to Memorix
          </h1>
          <p className="mt-2 text-slate-400">
            Let's get you set up. Choose how you want to proceed.
          </p>
        </div>

        <Tabs
          defaultValue="create"
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2 bg-slate-900/50 p-1 backdrop-blur-sm">
            <TabsTrigger
              value="create"
              className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
            >
              <Building2 className="mr-2 h-4 w-4" />
              Create Organization
            </TabsTrigger>
            <TabsTrigger
              value="join"
              className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Join Organization
            </TabsTrigger>
          </TabsList>

          <TabsContent value="create">
            <Card className="border-white/10 bg-slate-900/50 backdrop-blur-md">
              <CardHeader>
                <CardTitle className="text-xl text-white">
                  Create New Organization
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Set up a new organization space. You will be the
                  administrator.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateOrg} className="space-y-6">
                  <div className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="displayName" className="text-slate-300">
                          Your Name *
                        </Label>
                        <Input
                          id="displayName"
                          value={createForm.displayName}
                          onChange={(e) => {
                            setCreateForm({
                              ...createForm,
                              displayName: e.target.value,
                            });
                            setCreateErrors({
                              ...createErrors,
                              displayName: "",
                            });
                          }}
                          className={`border-white/10 bg-slate-950/50 text-white placeholder:text-slate-600 focus:border-purple-500 ${
                            createErrors.displayName ? "border-red-500" : ""
                          }`}
                        />
                        <FieldError error={createErrors.displayName} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="orgName" className="text-slate-300">
                          Organization Name *
                        </Label>
                        <Input
                          id="orgName"
                          placeholder="Acme Corp"
                          value={createForm.orgName}
                          onChange={(e) => {
                            setCreateForm({
                              ...createForm,
                              orgName: e.target.value,
                            });
                            setCreateErrors({ ...createErrors, orgName: "" });
                          }}
                          className={`border-white/10 bg-slate-950/50 text-white placeholder:text-slate-600 focus:border-purple-500 ${
                            createErrors.orgName ? "border-red-500" : ""
                          }`}
                        />
                        <FieldError error={createErrors.orgName} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="legalName" className="text-slate-300">
                        Legal Name (For Billing) *
                      </Label>
                      <Input
                        id="legalName"
                        placeholder="Acme Corporation Pvt Ltd"
                        value={createForm.legalName}
                        onChange={(e) => {
                          setCreateForm({
                            ...createForm,
                            legalName: e.target.value,
                          });
                          setCreateErrors({ ...createErrors, legalName: "" });
                        }}
                        className={`border-white/10 bg-slate-950/50 text-white placeholder:text-slate-600 focus:border-purple-500 ${
                          createErrors.legalName ? "border-red-500" : ""
                        }`}
                      />
                      <FieldError error={createErrors.legalName} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="addressLine1" className="text-slate-300">
                        Address Line 1 *
                      </Label>
                      <Input
                        id="addressLine1"
                        value={createForm.addressLine1}
                        onChange={(e) => {
                          setCreateForm({
                            ...createForm,
                            addressLine1: e.target.value,
                          });
                          setCreateErrors({
                            ...createErrors,
                            addressLine1: "",
                          });
                        }}
                        className={`border-white/10 bg-slate-950/50 text-white placeholder:text-slate-600 focus:border-purple-500 ${
                          createErrors.addressLine1 ? "border-red-500" : ""
                        }`}
                      />
                      <FieldError error={createErrors.addressLine1} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="addressLine2" className="text-slate-300">
                        Address Line 2
                      </Label>
                      <Input
                        id="addressLine2"
                        value={createForm.addressLine2}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            addressLine2: e.target.value,
                          })
                        }
                        className="border-white/10 bg-slate-950/50 text-white placeholder:text-slate-600 focus:border-purple-500"
                      />
                    </div>

                    <div className="grid gap-4 sm:grid-cols-3">
                      <div className="space-y-2">
                        <Label htmlFor="city" className="text-slate-300">
                          City *
                        </Label>
                        <Input
                          id="city"
                          value={createForm.city}
                          onChange={(e) => {
                            setCreateForm({
                              ...createForm,
                              city: e.target.value,
                            });
                            setCreateErrors({ ...createErrors, city: "" });
                          }}
                          className={`border-white/10 bg-slate-950/50 text-white placeholder:text-slate-600 focus:border-purple-500 ${
                            createErrors.city ? "border-red-500" : ""
                          }`}
                        />
                        <FieldError error={createErrors.city} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state" className="text-slate-300">
                          State *
                        </Label>
                        <select
                          id="state"
                          value={createForm.state}
                          onChange={(e) => {
                            setCreateForm({
                              ...createForm,
                              state: e.target.value,
                            });
                            setCreateErrors({ ...createErrors, state: "" });
                          }}
                          className={`flex h-10 w-full rounded-md border bg-slate-950/50 px-3 py-2 text-sm text-white shadow-sm focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                            createErrors.state
                              ? "border-red-500"
                              : "border-white/10 focus:border-purple-500"
                          }`}
                        >
                          <option value="">Select State</option>
                          {INDIAN_STATES.map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                        <FieldError error={createErrors.state} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pincode" className="text-slate-300">
                          Pincode *
                        </Label>
                        <Input
                          id="pincode"
                          maxLength={6}
                          placeholder="110001"
                          value={createForm.pincode}
                          onChange={(e) => {
                            setCreateForm({
                              ...createForm,
                              pincode: e.target.value.replace(/\D/g, ""),
                            });
                            setCreateErrors({ ...createErrors, pincode: "" });
                          }}
                          className={`border-white/10 bg-slate-950/50 text-white placeholder:text-slate-600 focus:border-purple-500 ${
                            createErrors.pincode ? "border-red-500" : ""
                          }`}
                        />
                        <FieldError error={createErrors.pincode} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="gstin" className="text-slate-300">
                        GSTIN
                      </Label>
                      <Input
                        id="gstin"
                        maxLength={15}
                        placeholder="22AAAAA0000A1Z5"
                        value={createForm.gstin}
                        onChange={(e) => {
                          setCreateForm({
                            ...createForm,
                            gstin: e.target.value.toUpperCase(),
                          });
                          setCreateErrors({ ...createErrors, gstin: "" });
                        }}
                        className={`border-white/10 bg-slate-950/50 text-white placeholder:text-slate-600 focus:border-purple-500 ${
                          createErrors.gstin ? "border-red-500" : ""
                        }`}
                      />
                      <FieldError error={createErrors.gstin} />
                      {!createErrors.gstin && (
                        <p className="text-[10px] text-slate-500">
                          Optional - 15 character GST Identification Number
                        </p>
                      )}
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label
                          htmlFor="contactPhone"
                          className="text-slate-300"
                        >
                          Contact Phone *
                        </Label>
                        <Input
                          id="contactPhone"
                          type="tel"
                          placeholder="9876543210"
                          value={createForm.contactPhone}
                          onChange={(e) => {
                            setCreateForm({
                              ...createForm,
                              contactPhone: e.target.value,
                            });
                            setCreateErrors({
                              ...createErrors,
                              contactPhone: "",
                            });
                          }}
                          className={`border-white/10 bg-slate-950/50 text-white placeholder:text-slate-600 focus:border-purple-500 ${
                            createErrors.contactPhone ? "border-red-500" : ""
                          }`}
                        />
                        <FieldError error={createErrors.contactPhone} />
                      </div>
                      <div className="space-y-2">
                        <Label
                          htmlFor="contactEmail"
                          className="text-slate-300"
                        >
                          Contact Email *
                        </Label>
                        <Input
                          id="contactEmail"
                          type="email"
                          placeholder="contact@company.com"
                          value={createForm.contactEmail}
                          onChange={(e) => {
                            setCreateForm({
                              ...createForm,
                              contactEmail: e.target.value,
                            });
                            setCreateErrors({
                              ...createErrors,
                              contactEmail: "",
                            });
                          }}
                          className={`border-white/10 bg-slate-950/50 text-white placeholder:text-slate-600 focus:border-purple-500 ${
                            createErrors.contactEmail ? "border-red-500" : ""
                          }`}
                        />
                        <FieldError error={createErrors.contactEmail} />
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                    size="lg"
                    disabled={busy}
                  >
                    {busy ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating Organization...
                      </>
                    ) : (
                      <>
                        Create Organization
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="join">
            <Card className="border-white/10 bg-slate-900/50 backdrop-blur-md">
              <CardHeader>
                <CardTitle className="text-xl text-white">
                  Join Existing Organization
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Enter the invitation code provided by your administrator.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleJoinOrg} className="space-y-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label
                        htmlFor="joinDisplayName"
                        className="text-slate-300"
                      >
                        Your Name *
                      </Label>
                      <Input
                        id="joinDisplayName"
                        value={joinForm.displayName}
                        onChange={(e) => {
                          setJoinForm({
                            ...joinForm,
                            displayName: e.target.value,
                          });
                          setJoinErrors({ ...joinErrors, displayName: "" });
                        }}
                        className={`border-white/10 bg-slate-950/50 text-white placeholder:text-slate-600 focus:border-indigo-500 ${
                          joinErrors.displayName ? "border-red-500" : ""
                        }`}
                      />
                      <FieldError error={joinErrors.displayName} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="code" className="text-slate-300">
                        Invitation Code *
                      </Label>
                      <div className="relative">
                        <Input
                          id="code"
                          maxLength={6}
                          placeholder="ABC123"
                          value={joinForm.code}
                          onChange={(e) => {
                            setJoinForm({
                              ...joinForm,
                              code: e.target.value
                                .toUpperCase()
                                .replace(/[^A-Z0-9]/g, ""),
                            });
                            setJoinErrors({ ...joinErrors, code: "" });
                          }}
                          className={`border-white/10 bg-slate-950/50 text-center text-2xl font-bold tracking-[0.5em] text-white placeholder:text-slate-700 focus:border-indigo-500 ${
                            joinErrors.code ? "border-red-500" : ""
                          }`}
                        />
                        {joinForm.code.length === 6 && !joinErrors.code && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                            <CheckCircle2 className="h-5 w-5" />
                          </div>
                        )}
                      </div>
                      <FieldError error={joinErrors.code} />
                      {!joinErrors.code && (
                        <p className="text-xs text-slate-500">
                          6-character code from your admin
                        </p>
                      )}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
                    size="lg"
                    disabled={busy}
                  >
                    {busy ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Joining...
                      </>
                    ) : (
                      <>
                        Join Organization
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
